<template>
  <div>
    <!-- Header with Privacy Toggle -->
    <div class="d-flex justify-content-between align-items-center mb-3">
      <div class="font-size-16 font-weight-bold text-dark">
        Summary Metrics
      </div>
      <div>
        <a-button size="small" type="default" @click="toggleMask">
          <i :class="isMasked ? 'fe fe-eye-off' : 'fe fe-eye'" class="mr-1"></i>
          {{ isMasked ? 'Show Amounts' : 'Hide Amounts' }}
        </a-button>
      </div>
    </div>

    <!-- Top KPI Cards Row -->
    <div class="row mb-4">
      <!-- Today's Collection Card -->
      <div :class="isAdminOrSuperAdmin ? 'col-xl-3 col-md-6 col-12' : 'col-xl-6 col-md-6 col-12'" class="mb-3">
        <div class="card border-0 shadow-sm text-white bg-primary h-100">
          <div class="card-body p-3">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <span class="text-uppercase font-size-12 font-weight-bold opacity-75">Collected Today</span>
              <span class="badge badge-light text-primary font-weight-bold">{{ data.today_transactions_count || 0 }} Receipts</span>
            </div>
            <h3 class="text-white mb-0 font-weight-bold">
              {{ displayAmount(data.today_collected_amount) }}
            </h3>
            <small class="opacity-75">Daily fee collection revenue</small>
          </div>
        </div>
      </div>

      <!-- Month's Collection Card -->
      <div :class="isAdminOrSuperAdmin ? 'col-xl-3 col-md-6 col-12' : 'col-xl-6 col-md-6 col-12'" class="mb-3">
        <div class="card border-0 shadow-sm text-white bg-success h-100">
          <div class="card-body p-3">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <span class="text-uppercase font-size-12 font-weight-bold opacity-75">Collected This Month</span>
              <i class="fe fe-calendar font-size-18 opacity-75"></i>
            </div>
            <h3 class="text-white mb-0 font-weight-bold">
              {{ displayAmount(data.month_collected_amount) }}
            </h3>
            <small class="opacity-75">Monthly total collection</small>
          </div>
        </div>
      </div>

      <!-- Total Paid Card (Restricted to Admin & Superadmin) -->
      <div class="col-xl-3 col-md-6 col-12 mb-3" v-if="isAdminOrSuperAdmin">
        <div class="card border-0 shadow-sm text-white bg-info h-100">
          <div class="card-body p-3">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <span class="text-uppercase font-size-12 font-weight-bold opacity-75">Total Paid (Session)</span>
              <span class="badge badge-light text-info font-weight-bold">{{ data.paid_count || 0 }} Paid</span>
            </div>
            <h3 class="text-white mb-0 font-weight-bold">
              {{ displayAmount(data.total_amount_paid) }}
            </h3>
            <small class="opacity-75">Total overall collected</small>
          </div>
        </div>
      </div>

      <!-- Total Due Card (Restricted to Admin & Superadmin) -->
      <div class="col-xl-3 col-md-6 col-12 mb-3" v-if="isAdminOrSuperAdmin">
        <div class="card border-0 shadow-sm text-white bg-danger h-100">
          <div class="card-body p-3">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <span class="text-uppercase font-size-12 font-weight-bold opacity-75">Pending / Overdue</span>
              <span class="badge badge-light text-danger font-weight-bold">{{ data.unpaid_count || 0 }} Unpaid</span>
            </div>
            <h3 class="text-white mb-0 font-weight-bold">
              {{ displayAmount(data.total_due_amount) }}
            </h3>
            <small class="opacity-75">Total uncollected balance</small>
          </div>
        </div>
      </div>
    </div>

    <!-- Charts & Breakdown Section -->
    <div class="row mb-4">
      <!-- Fee Status Donut Chart -->
      <div class="col-lg-6 col-12 mb-3">
        <div class="card h-100 shadow-sm border-0">
          <div class="card-header bg-white border-0 pb-0">
            <h6 class="font-weight-bold mb-0">Fee Payment Status Overview</h6>
          </div>
          <div class="card-body d-flex align-items-center justify-content-center">
            <vc-donut
              has-legend
              :total="100"
              :start-angle="0"
              :sections="sections"
            >
              Total Students (<b>{{ data.total_students || 0 }}</b>)
            </vc-donut>
          </div>
        </div>
      </div>

      <!-- Payment Mode Breakdown Today -->
      <div class="col-lg-6 col-12 mb-3">
        <div class="card h-100 shadow-sm border-0">
          <div class="card-header bg-white border-0 pb-0 d-flex justify-content-between align-items-center">
            <h6 class="font-weight-bold mb-0">Today's Collection Breakdown by Mode</h6>
          </div>
          <div class="card-body">
            <div class="mb-3">
              <div class="d-flex justify-content-between mb-1">
                <span class="font-weight-medium">💵 Cash Collection</span>
                <span class="font-weight-bold text-dark">{{ displayAmount(paymentBreakdown.cash) }}</span>
              </div>
              <a-progress :percent="getModePercentage(paymentBreakdown.cash)" status="active" strokeColor="#52c41a" />
            </div>

            <div class="mb-3">
              <div class="d-flex justify-content-between mb-1">
                <span class="font-weight-medium">💳 Online Payments (UPI / Cards)</span>
                <span class="font-weight-bold text-dark">{{ displayAmount(paymentBreakdown.online) }}</span>
              </div>
              <a-progress :percent="getModePercentage(paymentBreakdown.online)" status="active" strokeColor="#1890ff" />
            </div>

            <div class="mb-3">
              <div class="d-flex justify-content-between mb-1">
                <span class="font-weight-medium">📑 Cheque Collection</span>
                <span class="font-weight-bold text-dark">{{ displayAmount(paymentBreakdown.cheque) }}</span>
              </div>
              <a-progress :percent="getModePercentage(paymentBreakdown.cheque)" status="active" strokeColor="#faad14" />
            </div>

            <div class="mb-0" v-if="paymentBreakdown.other > 0">
              <div class="d-flex justify-content-between mb-1">
                <span class="font-weight-medium">🏦 Bank Transfer / Other</span>
                <span class="font-weight-bold text-dark">{{ displayAmount(paymentBreakdown.other) }}</span>
              </div>
              <a-progress :percent="getModePercentage(paymentBreakdown.other)" status="active" strokeColor="#722ed1" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Student Fee Table Section -->
    <div class="row">
      <div class="col-lg-12">
        <div class="card shadow-sm border-0">
          <div class="card-header bg-white border-0 d-flex justify-content-between align-items-center">
            <h6 class="font-weight-bold mb-0">Student Fee Summary Records</h6>
          </div>
          <div class="card-body p-0">
            <a-table
              :columns="cols"
              :data-source="tabledata"
              :pagination="false"
              :scroll="{ x: '100%' }"
              :loading="loading"
            >
              <template slot="payment_status" slot-scope="payment_status">
                <a-tag :color="payment_status === 'Paid' ? 'green' : 'volcano'">
                  {{ payment_status }}
                </a-tag>
              </template>
            </a-table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'

const cols = [
  {
    title: 'Code',
    dataIndex: 'code',
    scopedSlots: { customRender: 'code' },
  },
  {
    title: 'Roll No',
    dataIndex: 'roll_no',
    scopedSlots: { customRender: 'roll_no' },
  },
  {
    title: 'Student Name',
    dataIndex: 'name',
    scopedSlots: { customRender: 'name' },
  },
  {
    title: 'Fee Name',
    dataIndex: 'fee_name',
    scopedSlots: { customRender: 'fee_name' },
  },
  {
    title: 'Total Fee (₹)',
    dataIndex: 'total_amount',
    scopedSlots: { customRender: 'total_amount' },
  },
  {
    title: 'Paid Amount (₹)',
    dataIndex: 'paid_amount',
    scopedSlots: { customRender: 'paid_amount' },
  },
  {
    title: 'Status',
    dataIndex: 'payment_status',
    scopedSlots: { customRender: 'payment_status' },
  },
  {
    title: 'Payment Date',
    dataIndex: 'payment_date',
    scopedSlots: { customRender: 'payment_date' },
  },
]

export default {
  name: 'studentsFeeSummary',
  props: ['loading', 'data'],
  data: function() {
    return {
      tabledata: [],
      cols,
      sections: [{ label: 'Unpaid', value: 0 }, { label: 'Paid', value: 0 }],
      paymentBreakdown: { cash: 0, cheque: 0, online: 0, other: 0 },
      isMasked: localStorage.getItem('fee_summary_mask_amounts') !== 'false',
    }
  },
  computed: {
    ...mapState(['user']),
    userRole() {
      if (this.user && this.user.role) return this.user.role
      if (this.user && this.user.user && this.user.user.role) return this.user.user.role
      return ''
    },
    isAdminOrSuperAdmin() {
      const role = (this.userRole || '').toLowerCase()
      return ['admin', 'superadmin'].includes(role)
    },
  },
  watch: {
    data(newVal) {
      if (!newVal) return

      let total = newVal.total_students || 1
      let unpaid = newVal.unpaid_count || 0
      let paid = newVal.paid_count || 0

      let labelUnpaid = `Unpaid (${unpaid})`
      let labelPaid = `Paid (${paid})`

      this.sections = [
        { label: labelUnpaid, value: Math.round((unpaid * 100) / total) },
        { label: labelPaid, value: Math.round((paid * 100) / total) },
      ]

      if (newVal.payment_mode_breakdown) {
        this.paymentBreakdown = newVal.payment_mode_breakdown
      }

      if (newVal.students_fees && newVal.students_fees.data) {
        const formatCurr = this.formatCurrency
        this.tabledata = newVal.students_fees.data.map((e) => {
          return {
            name: (e.first_name || '') + ' ' + (e.last_name || ''),
            code: e.student_code || '-',
            roll_no: e.roll_no || '-',
            fee_name: e.fee_name || '-',
            total_amount: formatCurr(e.fee_total || e.amount || 0),
            paid_amount: formatCurr(e.total_paid_amount || e.receipt_paid_amount || 0),
            payment_status: e.payment_status === 1 ? 'Paid' : 'Unpaid',
            payment_date: e.payment_date || '-',
          }
        })
      }
    },
  },
  methods: {
    toggleMask() {
      this.isMasked = !this.isMasked
      localStorage.setItem('fee_summary_mask_amounts', this.isMasked)
    },
    displayAmount(value) {
      if (this.isMasked) {
        return '₹ ••••••'
      }
      return '₹' + this.formatCurrency(value)
    },
    formatCurrency(value) {
      if (value === undefined || value === null || isNaN(value)) return '0'
      return Number(value).toLocaleString('en-IN')
    },
    getModePercentage(amount) {
      const totalToday = this.data ? this.data.today_collected_amount : 0
      if (!totalToday || totalToday <= 0 || !amount) return 0
      return Math.min(100, Math.round((amount / totalToday) * 100))
    },
    handleBack: function() {
      this.$emit('onBack', 'index')
    },
  },
}
</script>

<style scoped>
.font-size-12 {
  font-size: 12px;
}
.font-size-16 {
  font-size: 16px;
}
.font-size-18 {
  font-size: 18px;
}
.font-weight-medium {
  font-weight: 500;
}
.opacity-75 {
  opacity: 0.85;
}
</style>
